import { DuoMeasurementValues, View } from "@novorender/api";

import {
    CenterLine,
    ColorStop,
    DeviationProjectConfig,
    PointToPointGroup,
    PointToTriangleGroup,
} from "apis/dataV2/deviationTypes";
import type { AlignmentId } from "features/alignment/types";
import { Uom } from "features/uom/hooks/useUom";
import { UnitType } from "features/uom/spec";
import { convertAndFormat } from "features/uom/utils";

import {
    DeviationForm,
    DeviationType,
    SubprofileGroup,
    UiCenterLine,
    UiDeviationConfig,
    UiDeviationProfile,
} from "./deviationTypes";

export const MAX_DEVIATION_PROFILE_COUNT = 6;
export const PARAM_BOUND_PRECISION = 0;
export const NEW_DEVIATION_ID = "new";
export const EMPTY_PARAMETER_BOUNDS = [0, 0] as [number, number];
export const DEVIATION_DECIMALS = 3;
export const DEVIATION_EPSILON = 0.1 ** DEVIATION_DECIMALS;

/** No alignment carries it, so the UI shows the subprofile's centerline as missing. */
const UNRESOLVED_OBJECT_ID = 0;

export function newDeviationForm(): DeviationForm {
    return {
        id: NEW_DEVIATION_ID,
        isCopyingFromProfileId: false,
        copyFromProfileId: { value: undefined },
        name: { value: "" },
        deviationType: { value: DeviationType.PointToTriangle },
        subprofiles: [newDeviationSubprofile()],
        colorSetup: {
            absoluteValues: false,
            colorStops: { value: [] },
        },
        hasFromAndTo: true,
        index: -1,
        subprofileIndex: 0,
        fixTriangleWinding: {
            enabled: false,
            fixRadius: { value: 0 },
        },
    };
}

export function newDeviationSubprofile(): SubprofileGroup {
    return {
        groups1: { value: [] },
        groups2: { value: [] },
        favorites: { value: [] },
        centerLine: {
            enabled: false,
            id: { value: undefined },
            parameterBounds: { value: EMPTY_PARAMETER_BOUNDS },
        },
        tunnelInfo: {
            enabled: false,
            heightToCeiling: { value: 0 },
        },
    };
}

export function profileToDeviationForm(profile: UiDeviationProfile): DeviationForm {
    return {
        id: profile.id,
        isCopyingFromProfileId: profile.copyFromProfileId !== undefined,
        copyFromProfileId: { value: profile.copyFromProfileId },
        name: { value: profile.name },
        subprofiles: profile.subprofiles.map((sp) => ({
            groups1: { value: sp.from.groupIds },
            groups2: { value: sp.to.groupIds },
            favorites: { value: sp.favorites },
            centerLine: sp.centerLine
                ? {
                      enabled: true,
                      id: { value: sp.centerLine.objectId },
                      brepId: sp.centerLine.brepId,
                      parameterBounds: { value: sp.centerLine.parameterBounds },
                  }
                : {
                      enabled: false,
                      id: { value: undefined },
                      parameterBounds: { value: EMPTY_PARAMETER_BOUNDS },
                  },
            tunnelInfo: {
                enabled: sp.heightToCeiling !== undefined,
                heightToCeiling: { value: sp.heightToCeiling ?? 0 },
            },
        })),
        colorSetup: {
            absoluteValues: profile.colors.absoluteValues,
            colorStops: { value: profile.colors.colorStops },
        },
        deviationType: { value: profile.deviationType },
        hasFromAndTo: profile.hasFromAndTo,
        index: profile.index,
        subprofileIndex: 0,
        fixTriangleWinding: {
            enabled: profile.fixTriangleWinding?.enabled ?? false,
            fixRadius: { value: profile.fixTriangleWinding?.fixRadius ?? 0 },
        },
    };
}

export function uiConfigToServerConfig(config: UiDeviationConfig): DeviationProjectConfig {
    const brepIds = new Set<string>();

    for (const profile of config.profiles) {
        for (const sp of profile.subprofiles) {
            if (sp.centerLine?.brepId) {
                brepIds.add(sp.centerLine.brepId);
            }
        }
    }

    const getProfileAttrs = (p: UiDeviationProfile) => {
        return {
            id: p.id,
            name: p.name,
            copyFromProfileId: p.copyFromProfileId,
            colors: p.colors,
            subprofiles: p.subprofiles.map((sp) => ({
                from: p.fromAndToSwapped ? sp.to : sp.from,
                to: p.fromAndToSwapped ? sp.from : sp.to,
                centerLine: sp.centerLine && toServerCenterLine(sp.centerLine),
                heightToCeiling: sp.heightToCeiling,
                favorites: sp.favorites,
            })),
            fromAndToSwapped: p.fromAndToSwapped,
            fixTriangleWinding: {
                enabled: p.fixTriangleWinding.enabled,
                fixRadius: p.fixTriangleWinding.enabled ? p.fixTriangleWinding.fixRadius : undefined,
            },
        } as PointToPointGroup | PointToTriangleGroup;
    };

    return {
        version: config.version,
        rebuildRequired: config.rebuildRequired,
        brepIds: [...brepIds],
        pointToTriangle: {
            groups: config.profiles
                .filter((p) => p.deviationType === DeviationType.PointToTriangle)
                .map(getProfileAttrs),
        },
        pointToPoint: {
            groups: config.profiles.filter((p) => p.deviationType === DeviationType.PointToPoint).map(getProfileAttrs),
        },
        runData: config.runData,
    };
}

/** `objectId` is regenerated on every build and nothing reads it back, so it is not persisted. */
function toServerCenterLine({ instanceId, brepId, parameterBounds }: UiCenterLine): CenterLine {
    return { instanceId, brepId, parameterBounds };
}

/**
 * Resolves every centerline in `config` against the alignments of the loaded scene, giving each
 * one the `brepId` and `objectId` of the alignment its `instanceId` names. An `instanceId` that
 * names no alignment is left unresolved rather than retried against the stored `brepId`. Only a
 * centerline saved before `instanceId` was recorded resolves by `brepId`, and it picks up the
 * matched alignment's `instanceId` so the next save upgrades it.
 */
export function reconcileCenterlines(config: UiDeviationConfig, alignments: AlignmentId[]): UiDeviationConfig {
    return {
        ...config,
        profiles: config.profiles.map((profile) => {
            if (!profile.subprofiles.some((sp) => sp.centerLine)) {
                return profile;
            }

            return {
                ...profile,
                subprofiles: profile.subprofiles.map((sp) => {
                    if (!sp.centerLine) {
                        return sp;
                    }

                    return { ...sp, centerLine: resolveCenterline(sp.centerLine, alignments) };
                }),
            };
        }),
    };
}

export function countCenterLines(config: UiDeviationConfig): { total: number; withoutInstanceId: number } {
    let total = 0;
    let withoutInstanceId = 0;

    for (const profile of config.profiles) {
        for (const sp of profile.subprofiles) {
            if (!sp.centerLine) {
                continue;
            }
            total++;
            if (!sp.centerLine.instanceId) {
                withoutInstanceId++;
            }
        }
    }

    return { total, withoutInstanceId };
}

function resolveCenterline(centerLine: UiCenterLine, alignments: AlignmentId[]): UiCenterLine {
    if (centerLine.instanceId) {
        const alignment = alignments.find((a) => a.instanceId === centerLine.instanceId);
        if (alignment?.brepId) {
            return { ...centerLine, brepId: alignment.brepId, objectId: alignment.id };
        }
        return { ...centerLine, objectId: UNRESOLVED_OBJECT_ID };
    }

    const alignment = alignments.find((a) => a.brepId === centerLine.brepId);
    return alignment
        ? { ...centerLine, instanceId: alignment.instanceId, objectId: alignment.id }
        : { ...centerLine, objectId: UNRESOLVED_OBJECT_ID };
}

export function serverConfigToUiConfig(
    config: DeviationProjectConfig,
    defaultColorStops: ColorStop[],
): UiDeviationConfig {
    const defaultColors = {
        absoluteValues: false,
        colorStops: defaultColorStops,
    };

    let profileIndex = 1;
    return {
        version: config.version ?? "1.0",
        rebuildRequired: config.rebuildRequired,
        runData: config.runData,
        profiles: [
            ...config.pointToTriangle.groups.map((g) =>
                populateMissingData({
                    id: g.id ?? window.crypto.randomUUID(),
                    name: g.name ?? `Deviation ${profileIndex++}`,
                    copyFromProfileId: g.copyFromProfileId,
                    deviationType: DeviationType.PointToTriangle,
                    index: -1,
                    subprofiles: g.subprofiles ?? [
                        {
                            from: {
                                objectIds: g.objectIds,
                                groupIds: g.groupIds,
                            },
                            to: {
                                objectIds: [],
                                groupIds: [],
                            },
                            favorites: g.favorites ?? [],
                            legendGroups: [],
                        },
                    ],
                    colors: g.colors ?? defaultColors,
                    hasFromAndTo: g.subprofiles !== undefined,
                    fromAndToSwapped: false,
                    fixTriangleWinding: {
                        enabled: g.fixTriangleWinding?.enabled ?? false,
                        fixRadius: g.fixTriangleWinding?.fixRadius ?? 0,
                    },
                } as UiDeviationProfile),
            ),
            ...config.pointToPoint.groups.map((g) =>
                populateMissingData({
                    id: g.id ?? window.crypto.randomUUID(),
                    name: g.name ?? `Deviation ${profileIndex++}`,
                    copyFromProfileId: g.copyFromProfileId,
                    deviationType: DeviationType.PointToPoint,
                    index: -1,
                    subprofiles: g.subprofiles?.map((sp) => ({
                        ...sp,
                        from: g.fromAndToSwapped ? sp.to : sp.from,
                        to: g.fromAndToSwapped ? sp.from : sp.to,
                    })) ?? [
                        {
                            from: g.fromAndToSwapped ? g.to : g.from,
                            to: g.fromAndToSwapped ? g.from : g.to,
                            favorites: g.favorites ?? [],
                            legendGroups: [],
                        },
                    ],
                    colors: g.colors ?? defaultColors,
                    hasFromAndTo: true,
                    fromAndToSwapped: g.fromAndToSwapped,
                    fixTriangleWinding: {
                        enabled: g.fixTriangleWinding?.enabled ?? false,
                        fixRadius: g.fixTriangleWinding?.fixRadius ?? 0,
                    },
                } as UiDeviationProfile),
            ),
        ],
    };
}

function populateMissingData(profile: UiDeviationProfile): UiDeviationProfile {
    return {
        ...profile,
        subprofiles: profile.subprofiles.map((sp) => {
            return {
                ...sp,
                favorites: sp.favorites ?? [],
            };
        }),
    };
}

export function colorStopSortFn(a: ColorStop, b: ColorStop) {
    return b.position - a.position;
}

export const DELETED_DEVIATION_LABEL = "[deleted]";

export function formatColorStopPos(uom: Uom, pos: number, absoluteValues: boolean) {
    return pos === 0
        ? "0"
        : absoluteValues
          ? `±${convertAndFormat(uom, Math.abs(pos), UnitType.length, { decimals: DEVIATION_DECIMALS })}`
          : pos > 0
            ? `+${convertAndFormat(uom, pos, UnitType.length, { decimals: DEVIATION_DECIMALS })}`
            : convertAndFormat(uom, pos, UnitType.length, { decimals: DEVIATION_DECIMALS });
}

export function sortColorStops(colorStops: ColorStop[], absoluteValues: boolean) {
    return colorStops.sort(absoluteValues ? (a, b) => Math.abs(b.position) - Math.abs(a.position) : colorStopSortFn);
}

export function accountForAbsValues(colorStops: ColorStop[]) {
    const absolute = colorStops.map((cs) => ({ ...cs, position: Math.abs(cs.position) }));
    const negatives = absolute.filter((cs) => cs.position > 0).map((cs) => ({ ...cs, position: -cs.position }));
    return [...absolute, ...negatives].sort(colorStopSortFn);
}

export async function getClosestAlignmentPoint(view: View, followPathId: number, centerLine: UiCenterLine) {
    if (view.measure) {
        const segment = await view.measure.core.pickCurveSegment(followPathId);
        if (segment) {
            const measure = await view.measure.core.measure(segment, {
                drawKind: "vertex",
                ObjectId: -1,
                parameter: view.renderState.camera.position,
            });
            if (measure) {
                const duoMeasure = measure as DuoMeasurementValues;
                if (duoMeasure.measureInfoB && typeof duoMeasure.measureInfoB.parameter === "number") {
                    return Math.max(
                        centerLine.parameterBounds[0],
                        Math.min(centerLine.parameterBounds[1], duoMeasure.measureInfoB.parameter),
                    );
                }
            }
        }
    }
}
